/**
 * Orbit API utilities
 * Handles all AJAX communication with WordPress backend
 */

export default class OrbitAPI {
	constructor() {
		this.config = window.orbitConfig || {};
		this.isPublicMode = this.config.mode === 'public';

		// In public mode, use direct URLs. In admin mode, use WordPress AJAX
		if (this.isPublicMode) {
			this.manifestUrl = this.config.manifestUrl || 'https://blocks.builtwithorbit.com/wp-json/orbit/v1/export/index';
			this.apiUrl = this.config.apiUrl || 'https://blocks.builtwithorbit.com/wp-json/orbit/v1/';
		} else {
			this.ajaxUrl = window.orbitAjax?.url || '/wp-admin/admin-ajax.php';
			this.nonce = window.orbitAjax?.nonce || '';

			if (!this.nonce) {
				console.warn('Orbit: No nonce found. AJAX requests may fail.');
			}
		}
	}

	/**
	 * Make AJAX request to WordPress
	 */
	async request(action, data = {}) {
		 const formData = new FormData();
		 formData.append('action', `orbit_${action}`);
		 formData.append('nonce', this.nonce);
		 
		 Object.keys(data).forEach(key => {
			 formData.append(key, data[key]);
		 });
	 
		 try {
			 const response = await fetch(this.ajaxUrl, {
				 method: 'POST',
				 body: formData,
				 credentials: 'same-origin',
				 headers: {
					 'X-Requested-With': 'XMLHttpRequest'
				 }
			 });
	 
			 const result = await response.json();
			 
			 if (!result.success) {
				 // Just throw the backend error message directly
				 throw new Error(result.data?.message || 'Unknown error');
			 }
	 
			 return result.data;
		 } catch (error) {
			 // If it's already our error, just re-throw it
			 if (error.message !== 'Unknown error') {
				 throw error;
			 }
			 
			 // Only add context for network/parsing errors
			 throw new Error('Connection failed. Please try again.');
		 }
	 }

	/**
	 * Get all blocks (manifest)
	 */
	async getManifest(clearCache = false) {
		// In public mode, fetch directly from manifest URL
		if (this.isPublicMode) {
			try {
				// Add cache buster if needed (manifest URL already has params)
				let url = this.manifestUrl;
				if (clearCache) {
					url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
				}

				const response = await fetch(url);

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const data = await response.json();
				return data.blocks || data || [];
			} catch (error) {
				throw new Error(`Failed to load blocks: ${error.message}`);
			}
		}

		// Admin mode: use WordPress AJAX
		return this.request('get_manifest', { clear_cache: clearCache ? '1' : '0' });
	}

	/**
	 * Get all categories
	 */
	async getCategories() {
		// In public mode, categories will be extracted from manifest in the app
		// Return empty array - the App component will handle this
		if (this.isPublicMode) {
			return [];
		}

		// Admin mode: use WordPress AJAX
		return this.request('get_categories');
	}

	/**
	 * Get blocks by category
	 */
	async getBlocksByCategory(category) {
		return this.request('get_blocks_by_category', { category });
	}

	/**
	 * Install a block
	 */
	async installBlock(slug) {
		return this.request('install_block', { slug });
	}

	/**
	 * Remove a block
	 */
	async removeBlock(slug) {
		return this.request('remove_block', { slug });
	}

	/**
	 * Install all blocks
	 */
	async installAll() {
		return this.request('install_all');
	}

	/**
	 * Remove all blocks
	 */
	async removeAll() {
		return this.request('remove_all');
	}
	
	/**
	 * Check for block updates
	 */
	async checkUpdates() {
		// No updates in public mode
		if (this.isPublicMode) {
			return [];
		}

		return this.request('check_updates');
	}
	
	/**
	 * Update a block
	 */
	async updateBlock(slug, overwrite = false) {
		return this.request('update_block', { slug, overwrite });
	}

	/**
	 * Bulk install a batch of blocks
	 */
	async bulkInstallBatch(slugs) {
		return this.request('bulk_install_batch', { slugs: JSON.stringify(slugs) });
	}

	/**
	 * Bulk remove a batch of blocks
	 */
	async bulkRemoveBatch(slugs) {
		return this.request('bulk_remove_batch', { slugs: JSON.stringify(slugs) });
	}

	/**
	 * Get all Figma files
	 */
	async getFigmaFiles() {
		return this.request('get_figma_files');
	}

	/**
	 * Get Figma categories
	 */
	async getFigmaCategories() {
		return this.request('get_figma_categories');
	}

	/**
	 * Get Figma file download URL
	 */
	async getFigmaDownloadUrl(slug) {
		return this.request('get_figma_download_url', { slug });
	}
}